import axios from "axios";
import { listContacts } from "../../../services/contactImportService";
import type { GhlContact, GhlSearchCursor } from "../../../services/contactImportService";
import type { CampaignStatusAttachment } from "../../../components/AIAgents/ChatWithAgent/types";

const SOCIAL_API_BASE =
  process.env.REACT_APP_SOCIAL_API_URL ||
  "https://bc631mqtg1.execute-api.us-east-1.amazonaws.com/Prod";

/**
 * Own axios instance, deliberately not the global one. The app-wide interceptor
 * signs the user out on any 401/403, and this module's 403 means "that campaign
 * belongs to another customer" — a bug to surface in the card, not a reason to
 * end the session. Same reasoning as the Facebook Graph client.
 */
const client = axios.create({ baseURL: SOCIAL_API_BASE });

const auth = (idToken: string) => ({ headers: { Authorization: `Bearer ${idToken}` } });

export interface StartCampaignInput {
  subject: string;
  bodyTemplate: string;
  contactIds: string[];
  /** Seconds between sends. The server clamps anything under 15. */
  pacingSeconds?: number;
  fallbackMap?: Record<string, string>;
}

export interface StartCampaignResult {
  campaign_id: string;
  status: string;
  total: number;
  estimated_duration_seconds: number;
}

/**
 * A stable key for one (draft, recipient list) pair.
 *
 * The server treats a repeated key inside its window as a resubmission rather
 * than a new campaign, so a double-clicked Send must produce the *same* key —
 * a random one per click would defeat the whole guard and email everyone twice.
 * Recipient order is normalised out for the same reason.
 */
export const campaignIdempotencyKey = (
  customerId: string,
  input: StartCampaignInput,
): string => {
  const raw = [
    customerId,
    input.subject,
    input.bodyTemplate,
    [...input.contactIds].sort().join(","),
  ].join("|");
  // djb2 — no crypto dependency, and collisions only matter within one customer's
  // 15-minute window, where the inputs are already near-identical when they collide.
  let hash = 5381;
  for (let i = 0; i < raw.length; i += 1) {
    hash = ((hash << 5) + hash + raw.charCodeAt(i)) >>> 0;
  }
  return `fe-${hash.toString(36)}-${raw.length}`;
};

/** POST /{customer_id}/ghl/outreach/send — starts a paced campaign.
 *  location_id is intentionally omitted: the server resolves the customer's
 *  connected sub-account, so the browser never handles one. */
export const startOutreachCampaign = async (
  customerId: string,
  idToken: string,
  input: StartCampaignInput,
): Promise<StartCampaignResult> => {
  const { data } = await client.post(
    `/${customerId}/ghl/outreach/send`,
    {
      subject: input.subject,
      body_template: input.bodyTemplate,
      contact_ids: input.contactIds,
      pacing_seconds: input.pacingSeconds ?? 30,
      fallback_map: input.fallbackMap ?? { first_name: "there" },
      idempotency_key: campaignIdempotencyKey(customerId, input),
    },
    auth(idToken),
  );
  return data;
};

/** GET /{customer_id}/ghl/outreach/{campaign_id}/status */
export const getOutreachCampaignStatus = async (
  customerId: string,
  idToken: string,
  campaignId: string,
): Promise<CampaignStatusAttachment> => {
  const { data } = await client.get(
    `/${customerId}/ghl/outreach/${campaignId}/status`,
    auth(idToken),
  );
  return data;
};

/** POST /{customer_id}/ghl/outreach/{campaign_id}/cancel — the worker stops
 *  chaining after the send already in flight. Nothing already sent is recalled. */
export const cancelOutreachCampaign = async (
  customerId: string,
  idToken: string,
  campaignId: string,
): Promise<{ campaign_id: string; status: string }> => {
  const { data } = await client.post(
    `/${customerId}/ghl/outreach/${campaignId}/cancel`,
    {},
    auth(idToken),
  );
  return data;
};

export interface RecipientSearchPage {
  contacts: GhlContact[];
  total: number;
  nextCursor: GhlSearchCursor | null;
}

/** One page of the customer's GHL contacts, searched server-side.
 *
 *  `query` goes to GHL, which matches it against name/email/phone across the
 *  whole location — not just whatever the picker happens to have loaded. Tags
 *  are not searchable there, so tag narrowing stays client-side over what is
 *  loaded, and the UI says so. */
export const searchOutreachRecipients = async (
  customerId: string,
  idToken: string,
  options: { query?: string; limit?: number; cursor?: GhlSearchCursor } = {},
): Promise<RecipientSearchPage> => {
  const page = await listContacts(customerId, idToken, {
    limit: options.limit ?? 100,
    query: options.query?.trim() || undefined,
    searchAfter: options.cursor,
  });
  return {
    contacts: page.contacts ?? [],
    total: page.total ?? 0,
    nextCursor: page.next_search_after ?? null,
  };
};

/** Preview one recipient's copy exactly as the worker will render it —
 *  same merge keys, same fallbacks, so the confirmation shows real text
 *  rather than `{{first_name}}`. */
export const renderMergeFields = (
  template: string,
  contact: GhlContact | undefined,
  fallbackMap: Record<string, string> = { first_name: "there" },
): string => {
  const [firstName = "", ...rest] = (contact?.name || "").trim().split(/\s+/);
  const values: Record<string, string> = {
    first_name: firstName || fallbackMap.first_name || "",
    last_name: rest.join(" ") || fallbackMap.last_name || "",
    company: contact?.company || fallbackMap.company || "",
    email: contact?.email || "",
    phone: contact?.phone || fallbackMap.phone || "",
  };
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) =>
    key in values ? values[key] : match,
  );
};
